'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { LanguageSelect } from './language-select';
import { LuStar } from 'react-icons/lu';
import { CiGrid42 } from 'react-icons/ci';
import { usePathname } from 'next/navigation';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const pathname = usePathname();

  const me = useCustomerStore(s => s.me);

  const { t } = useTranslations();
  const subscriptionHref = me ? ROUTES.SUBSCRIPTIONS() : ROUTES.SUBSCRIPTION();
  const subscriptionIsActive = pathname === getSafeHref(subscriptionHref);

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu" className={cn('ea-mobile-sidebar__trigger', props.className?.trigger)}>
          <CiGrid42 className="h-8 w-8 stroke-white text-white" />
        </SheetTrigger>
        <SheetContent
          side={props.side}
          className={cn(
            'flex flex-col items-start justify-start gap-12 overflow-scroll p-5',
            'ea-mobile-sidebar__content',
            'hide-scroll-bar',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'left-5 top-5',
            icon: 'text-black h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>
          {!!props.menu && (
            <div className="flex w-full flex-col items-start justify-start gap-6 pt-16">
              {props.menu.map(item => {
                const isActive = item.path ? pathname === getSafeHref(item.path) : false;
                const href = item.path ? getSafeHref(item.path) : '#';
                const isLink = item.path ? isRelativeHref(getSafeHref(item.path)) : false;
                const shouldScrollToHash = href.includes('#') && href !== '#';
                const className = cn(
                  'flex w-fit items-center justify-start gap-2 text-lg font-bold',
                  isActive
                    ? 'ea-category-menu-item__link--active cursor-context-menu text-black/100'
                    : 'text-black/85 hover:text-black/100',
                );
                const content = item.name;

                return (
                  <SheetClose key={`${item.type}-${item.path ?? item.name}`} asChild>
                    {isLink ? (
                      <Link prefetchOnHover
                        href={href}
                        className={className}
                        prefetch={!shouldScrollToHash}
                        scroll={shouldScrollToHash ? true : undefined}
                      >
                        {content}
                      </Link>
                    ) : item.path ? (
                      <a href={href} className={className}>
                        {content}
                      </a>
                    ) : (
                      <span className={className}>{content}</span>
                    )}
                  </SheetClose>
                );
              })}
              <SheetClose asChild>
                <Link
                  href={subscriptionHref}
                  className={cn(
                    'flex w-fit items-center justify-start gap-2 text-lg font-bold text-primary',
                    subscriptionIsActive &&
                      'ea-category-menu-item__link--active cursor-context-menu',
                  )}
                  prefetch
                >
                  <LuStar size={18} className="fill-primary stroke-primary text-primary" />
                  {t('text1917')}
                </Link>
              </SheetClose>
            </div>
          )}
          {!me && (
            <SheetClose asChild className="w-full">
              <Link
                prefetch={true}
                href={ROUTES.SIGN_IN()}
                className="flex min-h-12 w-full items-center justify-center border border-primary text-center font-bold text-primary hover:bg-primary/10"
              >
                {t('text190')}
              </Link>
            </SheetClose>
          )}
          <p className="text-sm font-bold text-black">
            {t('text1924')} <span className="text-primary">{generalLayout.contact?.email}</span>
          </p>
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <p className="font-heading text-2xl font-medium text-black">{t('text492')}</p>
            <SocialIcons
              className={{
                button:
                  'flex h-[50px] w-[50px] items-center justify-center rounded-none border-none bg-black/10 hover:bg-black/30 sm:h-[52px] sm:w-[52px]',
                icon: 'fill-black stroke-black text-black',
              }}
            />
          </div>
          <LanguageSelect white={false} />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
